<template>
  <b-modal
      :visible="value"
      :title="step === 1 ? `Объединить «${artist.title}» с …` : 'Объединение артистов'"
      :size="step === 1 ? 'md' : 'lg'"
      centered
      hide-footer
      @change="$emit('input', $event)"
      @hidden="reset"
  >
    <!-- Шаг 1: выбор второго артиста -->
    <div v-if="step === 1">
      <b-form-group label="Артист для объединения">
        <v-select
            v-model="selectedArtist"
            :options="searchOptions"
            :filterable="false"
            :loading="isSearching"
            label="title"
            placeholder="Начните вводить название…"
            @search="onSearch"
        >
          <template #option="option">
            <div class="d-flex justify-content-between align-items-center">
              <span>{{ option.title }}</span>
              <small class="text-muted ml-1">ID {{ option.id }} · треков: {{ option.tracks_count || 0 }}</small>
            </div>
          </template>
          <template #no-options>Введите минимум 2 символа для поиска</template>
        </v-select>
      </b-form-group>
      <div class="d-flex justify-content-end mt-2">
        <b-button variant="outline-secondary" class="mr-1" @click="$emit('input', false)">Отмена</b-button>
        <b-button variant="primary" :disabled="!selectedArtist" @click="goToCompare">Далее</b-button>
      </div>
    </div>

    <!-- Шаг 2: сравнение полей -->
    <div v-else-if="sourceArtist">
      <b-table-simple responsive class="mb-1">
        <b-thead>
          <b-tr>
            <b-th style="width: 22%">Поле</b-th>
            <b-th style="width: 28%">
              <div
                  class="merge-header p-75 rounded"
                  :class="{ 'merge-header-active': baseIs === 1 }"
                  @click="baseIs = 1"
              >
                <div class="font-weight-bold text-truncate">{{ artist.title }}</div>
                <b-badge :variant="baseIs === 1 ? 'success' : 'light-secondary'">
                  {{ baseIs === 1 ? 'Базовый артист' : 'Объединяемый' }}
                </b-badge>
              </div>
            </b-th>
            <b-th style="width: 28%">
              <div
                  class="merge-header p-75 rounded"
                  :class="{ 'merge-header-active': baseIs === 2 }"
                  @click="baseIs = 2"
              >
                <div class="font-weight-bold text-truncate">{{ sourceArtist.title }}</div>
                <b-badge :variant="baseIs === 2 ? 'success' : 'light-secondary'">
                  {{ baseIs === 2 ? 'Базовый артист' : 'Объединяемый' }}
                </b-badge>
              </div>
            </b-th>
            <b-th style="width: 22%">Результат</b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr v-for="field in diffFields" :key="field.key">
            <b-td class="text-muted">{{ field.label }}</b-td>
            <b-td>
              <b-form-checkbox
                  :checked="selection[field.key] === 1"
                  @change="selectValue(field.key, 1)"
              >
                {{ formatValue(field, artist[field.key]) }}
              </b-form-checkbox>
            </b-td>
            <b-td>
              <b-form-checkbox
                  :checked="selection[field.key] === 2"
                  @change="selectValue(field.key, 2)"
              >
                {{ formatValue(field, sourceArtist[field.key]) }}
              </b-form-checkbox>
            </b-td>
            <b-td class="font-weight-bold">{{ formatValue(field, resultValue(field.key)) }}</b-td>
          </b-tr>
          <b-tr v-if="!diffFields.length">
            <b-td colspan="4" class="text-center text-muted">
              Все поля совпадают — выберите только базового артиста
            </b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>

      <div class="text-muted font-small-3 mb-1">
        Базовый артист сохраняется (его ID остаётся), второй будет удалён. Треки, обложки, фото,
        списки, составы и внешние ссылки переносятся; при дублях приоритет у базового.
        Страница неактуального названия в Notion будет заархивирована.
      </div>

      <div class="d-flex justify-content-between">
        <b-button variant="flat-secondary" @click="step = 1">Назад</b-button>
        <div>
          <b-button variant="outline-secondary" class="mr-1" @click="$emit('input', false)">Отмена</b-button>
          <b-button variant="primary" :disabled="isMerging" @click="submitMerge">
            <b-spinner v-if="isMerging" small class="mr-50"/>
            Объединить
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal, BButton, BBadge, BFormGroup, BFormCheckbox, BSpinner,
  BTableSimple, BThead, BTbody, BTr, BTh, BTd,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ref, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'

// Поля из ArtistMergeService::MERGEABLE_FIELDS с подписями для таблицы сравнения
const MERGE_FIELDS = [
  { key: 'title', label: 'Название' },
  { key: 'popularity', label: 'Популярность (Last.fm)' },
  { key: 'onehitwonder', label: 'One Hit Wonder', boolean: true },
  { key: 'russian', label: 'Русский', boolean: true },
  { key: 'tags_only', label: 'Только теги', boolean: true },
  { key: 'difficulty_rating', label: 'Сложность' },
  { key: 'is_cover_artist', label: 'Кавер-артист', boolean: true },
  { key: 'skip_originals', label: 'Пропускать оригиналы', boolean: true },
  { key: 'spotify_playcount', label: 'Прослушивания Spotify' },
  { key: 'yandex_monthly_listeners', label: 'Слушатели Яндекса/мес' },
]

export default {
  name: 'MergeArtistsModal',
  components: {
    BModal, BButton, BBadge, BFormGroup, BFormCheckbox, BSpinner,
    BTableSimple, BThead, BTbody, BTr, BTh, BTd, vSelect,
  },
  props: {
    value: { type: Boolean, default: false },
    artist: { type: Object, required: true },
  },
  setup(props, { emit }) {
    const toast = useToast()

    const step = ref(1)
    const selectedArtist = ref(null)
    const sourceArtist = ref(null)
    const searchOptions = ref([])
    const isSearching = ref(false)
    const isMerging = ref(false)
    const baseIs = ref(1)
    const selection = ref({})
    let searchTimer = null

    const isEmptyValue = (field, value) => {
      if (field.boolean) return !value
      return value === null || value === undefined || value === ''
    }

    // Только отличающиеся поля (булевы сравниваем по !!, 0 и null различаем)
    const diffFields = computed(() => {
      if (!sourceArtist.value) return []
      return MERGE_FIELDS.filter(field => {
        const own = field.boolean ? !!props.artist[field.key] : props.artist[field.key]
        const other = field.boolean ? !!sourceArtist.value[field.key] : sourceArtist.value[field.key]
        return String(own ?? '') !== String(other ?? '')
      })
    })

    const applyDefaultSelection = () => {
      const defaults = {}
      diffFields.value.forEach(field => {
        const ownEmpty = isEmptyValue(field, props.artist[field.key])
        const otherEmpty = isEmptyValue(field, sourceArtist.value[field.key])
        // По умолчанию значение Артиста 1, но пустое проигрывает непустому
        defaults[field.key] = (ownEmpty && !otherEmpty) ? 2 : 1
      })
      selection.value = defaults
    }

    const onSearch = (search, loading) => {
      if (!search || search.length < 2) return
      if (searchTimer) clearTimeout(searchTimer)
      searchTimer = setTimeout(async () => {
        isSearching.value = true
        loading(true)
        try {
          const response = await store.dispatch('artists/fetchArtists', { q: search, per_page: 10 })
          const items = response.data.data || []
          searchOptions.value = items.filter(item => item.id !== props.artist.id)
        } catch (error) {
          searchOptions.value = []
        } finally {
          isSearching.value = false
          loading(false)
        }
      }, 500)
    }

    // GET /artist уже отдаёт полный ArtistResource — отдельный запрос деталей не нужен
    const goToCompare = () => {
      sourceArtist.value = selectedArtist.value
      baseIs.value = 1
      applyDefaultSelection()
      step.value = 2
    }

    const selectValue = (key, who) => {
      // Радио-поведение: щелчок по уже выбранной галке оставляет её выбранной
      selection.value = { ...selection.value, [key]: who }
    }

    const resultValue = key => (selection.value[key] === 2 ? sourceArtist.value[key] : props.artist[key])

    const formatValue = (field, value) => {
      if (field.boolean) return value ? 'Да' : 'Нет'
      if (value === null || value === undefined || value === '') return '—'
      return value
    }

    const submitMerge = async () => {
      const base = baseIs.value === 1 ? props.artist : sourceArtist.value
      const other = baseIs.value === 1 ? sourceArtist.value : props.artist
      const fields = {}
      diffFields.value.forEach(field => {
        fields[field.key] = resultValue(field.key)
      })

      isMerging.value = true
      try {
        await store.dispatch('artists/mergeArtists', {
          baseId: base.id,
          sourceArtistId: other.id,
          fields,
        })
        toast({
          component: ToastificationContent,
          props: { title: 'Артисты объединены', icon: 'CheckIcon', variant: 'success' },
        })
        emit('merged', base.id)
        emit('input', false)
      } catch (error) {
        const message = error.response?.data?.message || 'Ошибка объединения артистов'
        toast({
          component: ToastificationContent,
          props: { title: message, icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      } finally {
        isMerging.value = false
      }
    }

    const reset = () => {
      step.value = 1
      selectedArtist.value = null
      sourceArtist.value = null
      searchOptions.value = []
      selection.value = {}
      baseIs.value = 1
    }

    return {
      step,
      selectedArtist,
      sourceArtist,
      searchOptions,
      isSearching,
      isMerging,
      baseIs,
      selection,
      diffFields,
      onSearch,
      goToCompare,
      selectValue,
      resultValue,
      formatValue,
      submitMerge,
      reset,
    }
  },
}
</script>

<style scoped>
.merge-header {
  cursor: pointer;
  border: 1px solid rgba(115, 103, 240, 0.2);
}
.merge-header-active {
  background-color: rgba(115, 103, 240, 0.12);
  border-color: #7367f0;
}
</style>
